// Create a shift-swap request from the Shift Swaps page.
// Requester = the current user; target = who takes the shift.

import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Modal, Select, Input, Button, useToast } from '@bbpos/ui';
import { useShifts, useCreateSwap } from '@/features/shifts';
import { userService } from '@/services/user.service';
import { useAuthStore } from '@/stores/use-auth-store';
import { ApiError } from '@/lib/api-client';

interface RequestSwapModalProps {
  open: boolean;
  onClose: () => void;
  tenantId?: string;
  /** Current user id — excluded from the "target" list (can't swap with yourself). */
  currentUserId?: string;
}

export function RequestSwapModal({ open, onClose, tenantId, currentUserId }: RequestSwapModalProps) {
  const { toast } = useToast();
  const createSwap = useCreateSwap();
  const profileId = useAuthStore((s) => s.profile?.id ?? '');
  const meId = currentUserId ?? profileId;

  const [shiftId, setShiftId] = useState('');
  const [targetUserId, setTargetUserId] = useState('');
  const [date, setDate] = useState('');
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) {
      setShiftId('');
      setTargetUserId('');
      setDate('');
      setReason('');
    }
  }, [open]);

  const shiftsQuery = useShifts({ page: 1, is_active: 'true' });
  const usersQuery = useQuery({
    queryKey: ['users', tenantId, 'swap-target'],
    queryFn: () => userService.list({ tenant_id: tenantId }),
    enabled: open,
    staleTime: 30_000,
  });

  const shiftOptions = [
    { value: '', label: 'Select a shift…' },
    ...(shiftsQuery.data?.data ?? []).map((s) => ({ value: s.id, label: s.name })),
  ];
  // Exclude yourself — you swap TO someone else.
  const userOptions = [
    { value: '', label: 'Select who takes the shift…' },
    ...(usersQuery.data?.data ?? [])
      .filter((u) => u.id !== meId)
      .map((u) => ({ value: u.id, label: `${u.name} (${u.role})` })),
  ];

  const canSubmit = Boolean(shiftId && targetUserId && date) && !createSwap.isPending;

  async function handleSubmit() {
    // Explicit UUID guards — a blank shift/target/requester would be rejected by
    // the DB (uuid type) but the API can still 2xx; block it here with a clear
    // message instead of a misleading success.
    const isUuid = (v: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);
    if (!isUuid(shiftId)) {
      toast({ title: 'Select a valid shift.', variant: 'warning' });
      return;
    }
    if (!isUuid(targetUserId)) {
      toast({ title: 'Select who takes the shift.', variant: 'warning' });
      return;
    }
    if (!date) {
      toast({ title: 'Pick a date.', variant: 'warning' });
      return;
    }
    if (!canSubmit) {
      toast({ title: 'Pick a shift, a date, and who takes the shift.', variant: 'warning' });
      return;
    }
    try {
      await createSwap.mutateAsync({
        shift_id: shiftId,
        shift_date: date,
        target_user_id: targetUserId,
        reason: reason.trim() || undefined,
      });
      toast({ title: 'Swap request created', variant: 'success' });
      onClose();
    } catch (err) {
      toast({
        title: err instanceof ApiError ? err.message : 'Failed to create swap request',
        variant: 'error',
      });
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Request Shift Swap">
      <div className="space-y-4">
        <div>
          <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">Shift *</label>
          <Select value={shiftId} onChange={(v) => setShiftId(typeof v === 'string' ? v : '')} options={shiftOptions} />
        </div>
        <div>
          <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">Date *</label>
          <Input type="date" value={date} onChange={(v) => setDate(typeof v === 'string' ? v : '')} />
        </div>
        <div>
          <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">Swap to (who takes the shift) *</label>
          <Select value={targetUserId} onChange={(v) => setTargetUserId(typeof v === 'string' ? v : '')} options={userOptions} />
        </div>
        <div>
          <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">Reason</label>
          <Input value={reason} onChange={(v) => setReason(typeof v === 'string' ? v : '')} placeholder="Optional" />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={handleSubmit} disabled={!canSubmit}>
            {createSwap.isPending ? 'Requesting…' : 'Request Swap'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
